import i18n from '../../i18n';
import { canonicalizeStatus } from '../../utils/actionItemStatus';

/**
 * Whether Send to Client is available on a review, and — when it is not — WHY, in words a reviewer can
 * act on (docs/specs/review-document-integrity.md S3). The button used to be greyed out with the reason
 * only in a hover tooltip on a control that cannot take focus, so Elvia saw a dead button on a review
 * whose documents were all present (2026-10-21). A disabled state with no reason is never returned.
 */

/** The server's terminal set (ActionItemStatusHelper.TerminalStatuses): off everyone's plate. */
const RESOLVED = new Set(['completed', 'cancelled']);

export interface GateActionItem {
  id: string;
  title: string;
  status: string | null | undefined;
}

export interface SendGateInput {
  isCompleted: boolean;
  /** Documents SAVED on the review, as the server returned them. */
  documentCount: number;
  /**
   * The action items linked to this review, or null when they have not loaded (still loading, or the request
   * failed). Null closes the gate: a list that never arrived is not "no open action items" (P6, 2026-10-30).
   */
  actionItems: GateActionItem[] | null;
}

/** Which fact closed the gate; null when it is open. */
export type SendGateKind = 'completed' | 'no-documents' | 'actions-unknown' | 'open-actions';

export interface SendGate {
  enabled: boolean;
  kind: SendGateKind | null;
  /** Present exactly when `enabled` is false. */
  reason: string | null;
  /** The action items holding the send back, so the page can point at them. */
  blocking: GateActionItem[];
}

export function sendToClientGate({ isCompleted, documentCount, actionItems }: SendGateInput): SendGate {
  const blocking = (actionItems ?? []).filter((a) => !RESOLVED.has(canonicalizeStatus(a.status)));

  if (isCompleted) return { enabled: false, kind: 'completed', reason: i18n.t('sendGate.completed'), blocking };
  if (documentCount <= 0) return { enabled: false, kind: 'no-documents', reason: i18n.t('sendGate.noDocuments'), blocking };
  if (actionItems === null) return { enabled: false, kind: 'actions-unknown', reason: i18n.t('sendGate.actionsUnknown'), blocking };
  if (blocking.length > 0) {
    const titles = blocking.map((a) => i18n.t('sendGate.quoted', { title: a.title })).join(', ');
    return { enabled: false, kind: 'open-actions', reason: i18n.t('sendGate.openActions', { count: blocking.length, titles }), blocking };
  }
  return { enabled: true, kind: null, reason: null, blocking: [] };
}
